import { useEffect, useMemo, useState } from "react";
import { RoleShell } from "@/components/RoleShell";
import { StatusPill } from "@/components/StatusPill";
import { useAppointments } from "@/hooks/useAppointments";
import { useStaff, useServices, useInventory } from "@/hooks/useCatalog";
import { useBranch } from "@/contexts/BranchContext";
import { api, unwrap } from "@/lib/api";
import {
  AlertTriangle, Sparkles, FlaskConical, Check, ChevronRight, Send, Award, Save, Clock, Pencil, X,
} from "lucide-react";
import { toast } from "sonner";
import { STYLIST } from "@/constants/testIds";
import { BirthdaySection } from "@/components/BirthdaySection";
import { MyCommissionBox } from "@/components/MyCommissionBox";

export default function Stylist() {
  const { activeBranchId } = useBranch();
  const { items, update } = useAppointments({ branchId: activeBranchId });
  const { staff } = useStaff();
  const { services } = useServices();
  const { items: inventory } = useInventory();

  const stylistList = useMemo(() => staff.filter((s) => ["stylist", "therapist", "nail_tech"].includes(s.role)), [staff]);
  const [activeStylistId, setActiveStylistId] = useState("");
  useEffect(() => {
    if (!activeStylistId && stylistList.length) setActiveStylistId(stylistList[0].id);
  }, [stylistList, activeStylistId]);

  const myQueue = useMemo(() => {
    if (!activeStylistId) return [];
    return items.filter((a) => a.assigned_stylist_id === activeStylistId && ["checked-in", "in-service", "payment-pending", "booked"].includes(a.status));
  }, [items, activeStylistId]);

  const [selectedId, setSelectedId] = useState(null);
  useEffect(() => {
    if (!selectedId && myQueue.length) setSelectedId(myQueue[0].id);
  }, [myQueue, selectedId]);

  const selected = items.find((a) => a.id === selectedId) || myQueue[0];
  const [tab, setTab] = useState("service");

  const [formula, setFormula] = useState("");
  const [notes, setNotes] = useState("");
  const [chosenServices, setChosenServices] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [history, setHistory] = useState([]);
  const [client, setClient] = useState(null);
  const [editClientOpen, setEditClientOpen] = useState(false);

  // Sync editable fields when selection changes
  useEffect(() => {
    if (!selected) return;
    setFormula(selected.formula_notes || "");
    setNotes(selected.service_notes || "");
    setChosenServices((selected.services_requested || []).slice());
    setProducts((selected.products_used || []).slice());
  }, [selectedId, selected?.id]); // eslint-disable-line

  // Fetch history + profile when client_id changes
  useEffect(() => {
    if (!selected?.client_id) { setHistory([]); setClient(null); return; }
    api.get(`/clients/${selected.client_id}/history`).then((r) => setHistory(unwrap(r) || [])).catch(() => {});
    api.get(`/clients/${selected.client_id}`).then((r) => setClient(unwrap(r))).catch(() => {});
  }, [selected?.client_id]);

  const total = useMemo(() => {
    const map = Object.fromEntries(services.map((s) => [s.name, s.base_price]));
    return chosenServices.reduce((sum, name) => sum + (map[name] || 0), 0);
  }, [chosenServices, services]);

  if (!selected) {
    return (
      <RoleShell title="Stylist / Therapist">
        <div className="max-w-[1700px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6" data-testid={STYLIST.root}>
          <StylistPicker stylistList={stylistList} activeStylistId={activeStylistId} setActiveStylistId={setActiveStylistId} />
          <div className="glass-primary rounded-[24px] p-10 text-center">
            <div className="font-display text-[22px] font-semibold">No active clients</div>
            <p className="font-inter text-[14px] text-[var(--color-text-muted)] mt-2">When the receptionist assigns you a client, they'll appear here.</p>
          </div>
        </div>
      </RoleShell>
    );
  }

  const beginService = async () => {
    await update(selected.id, { status: "in-service" });
    toast.success("Service started", { description: `${selected.client_name} · ${selected.chair_room || "—"}` });
  };

  const saveOnly = async () => {
    await update(selected.id, {
      service_notes: notes,
      formula_notes: formula,
      services_requested: chosenServices,
      products_used: products,
    });
    toast.success("Notes saved");
  };

  const sendToBilling = async () => {
    const map = Object.fromEntries(services.map((s) => [s.name, s]));
    const rendered = chosenServices.map((name) => ({
      service_id: map[name]?.id || null,
      service_name: name,
      price: map[name]?.base_price || 0,
    }));
    await update(selected.id, {
      service_notes: notes,
      formula_notes: formula,
      services_requested: chosenServices,
      services_rendered: rendered,
      products_used: products,
      amount_due: total,
      status: "payment-pending",
    });
    toast.success("Sent to billing", { description: `₹${total} due · invoice will be generated on payment` });
  };

  const completeNoCharge = async () => {
    await update(selected.id, { amount_due: 0, status: "completed", paid: true });
    toast.success("Service complete · no charge applied");
  };

  return (
    <RoleShell title="Stylist / Therapist" showFloorMap>
      <div className="max-w-[1700px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6" data-testid={STYLIST.root}>
        <aside>
          <StylistPicker stylistList={stylistList} activeStylistId={activeStylistId} setActiveStylistId={setActiveStylistId} />

          <div className="glass-primary rounded-[20px] p-5 mt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="overline">My queue</div>
              <span className="px-2 py-0.5 rounded-full bg-[var(--color-gold-mid)]/15 text-[var(--color-gold-deeper)] font-mono text-[11px]">{myQueue.length}</span>
            </div>
            <div className="space-y-2.5">
              {myQueue.length === 0 && <div className="font-inter text-[13px] text-[var(--color-text-muted)] py-2">No clients assigned right now.</div>}
              {myQueue.map((a) => {
                const isSel = selected?.id === a.id;
                return (
                  <button key={a.id} onClick={() => { setSelectedId(a.id); setTab("service"); }}
                          className={`w-full text-left rounded-xl p-3 transition border ${isSel ? "bg-[var(--color-gold-mid)]/10 border-[var(--color-gold-mid)]/40" : "bg-white/55 border-white/80 hover:bg-white/75"}`}
                          data-testid={STYLIST.queueCard(a.id)}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-[14px] text-[var(--color-mauve-deep)]">{a.token_number}</span>
                      <StatusPill status={a.status} size="sm" />
                    </div>
                    <div className="font-sora font-semibold text-[14px] mt-1.5 truncate">{a.client_name}</div>
                    <div className="font-inter text-[11.5px] text-[var(--color-text-muted)] truncate">{(a.services_requested || []).join(" + ")}</div>
                    {a.is_emergency && (
                      <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-sora font-bold uppercase tracking-[0.2em] text-[var(--color-danger)]">
                        <AlertTriangle size={11} /> Emergency
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-4">
            <MyCommissionBox staffId={activeStylistId} />
          </div>
        </aside>

        <section>
          {/* Client header */}
          <div className="glass-primary rounded-[20px] p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono text-[20px] text-[var(--color-mauve-deep)]">{selected.token_number}</span>
                  <StatusPill status={selected.status} />
                  {selected.is_emergency && <span className="status-pill" style={{ background: "rgba(185,28,28,0.10)", color: "#991B1B" }}><span className="dot" style={{ background: "#B91C1C", animation: "pulseDot 1.4s infinite" }} />EMERGENCY</span>}
                </div>
                <h1 className="font-display font-bold text-[30px] tracking-[-0.02em] mt-2">{selected.client_name}</h1>
                <div className="font-inter text-[13px] text-[var(--color-text-muted)] mt-1">
                  {selected.client_phone} · {selected.gender || "—"} · {(selected.services_requested || []).join(" + ")} {selected.chair_room && <>· {selected.chair_room}</>}
                </div>
              </div>
              {selected.status !== "in-service" && selected.status !== "completed" && selected.status !== "payment-pending" && (
                <button className="btn-3d btn-gold-3d btn-lg" onClick={beginService} data-testid={STYLIST.beginServiceBtn}>
                  <Sparkles size={16} /> Begin service
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-5 flex items-center gap-2">
            <TabButton active={tab === "service"} onClick={() => setTab("service")} testid={STYLIST.tabService}>Service</TabButton>
            <TabButton active={tab === "history"} onClick={() => setTab("history")} testid={STYLIST.tabHistory}>Client History · {history.length}</TabButton>
            <TabButton active={tab === "profile"} onClick={() => setTab("profile")} testid={STYLIST.tabProfile}>Client Profile</TabButton>
          </div>

          {tab === "service" && (
            <div className="space-y-5 mt-5">
              <ServiceChecklist services={services} chosen={chosenServices} setChosen={setChosenServices} total={total} />

              <div className="glass-primary rounded-[20px] p-6">
                <div className="flex items-center gap-2 overline mb-2"><FlaskConical size={13} /> Formula notes</div>
                <textarea value={formula} onChange={(e) => setFormula(e.target.value)} rows={3}
                          placeholder="e.g. Base 7/0 + 6/0 (1:1) with 20vol · Foils 9/03 cool toner · 35 min"
                          className="w-full px-3 py-2.5 rounded-xl bg-white/70 border border-white/80 font-inter text-[13.5px] outline-none"
                          data-testid={STYLIST.formulaInput} />
              </div>

              <div className="glass-primary rounded-[20px] p-6">
                <div className="flex items-center gap-2 overline mb-2"><Sparkles size={13} /> Service notes</div>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                          placeholder="Notes for billing & next visit (preferences, requests, technique)…"
                          className="w-full px-3 py-2.5 rounded-xl bg-white/70 border border-white/80 font-inter text-[13.5px] outline-none" />
              </div>

              <ProductLogger inventory={inventory} products={products} setProducts={setProducts} search={productSearch} setSearch={setProductSearch} />

              <div className="flex items-center justify-end gap-3 flex-wrap">
                <button className="btn-3d btn-glass-3d" onClick={saveOnly} data-testid={STYLIST.saveNotesBtn}>
                  <Save size={16} /> Save notes
                </button>
                <button className="btn-3d btn-success-3d" onClick={completeNoCharge}>
                  <Check size={16} /> Complete · no charge
                </button>
                <button className="btn-3d btn-gold-3d btn-lg" onClick={sendToBilling} data-testid={STYLIST.applySendBtn}>
                  <Send size={16} /> Apply &amp; send to billing · ₹{total}
                </button>
              </div>
            </div>
          )}

          {tab === "history" && (
            <div className="mt-5 space-y-3">
              {history.length === 0 ? (
                <div className="glass-primary rounded-[20px] p-8 text-center font-inter text-[14px] text-[var(--color-text-muted)]">First visit — no history yet.</div>
              ) : history.map((h, i) => (
                <div key={h.id} className="glass-primary rounded-[20px] p-5">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="font-sora font-semibold text-[15px]">{(h.services_requested || []).join(" + ")}</div>
                    <div className="font-mono text-[12.5px] text-[var(--color-text-muted)]">{h.completed_at ? new Date(h.completed_at).toLocaleString([], { month: "short", day: "numeric", year: "numeric" }) : "—"}</div>
                  </div>
                  <div className="font-inter text-[12.5px] text-[var(--color-text-muted)] mt-1">with {h.assigned_stylist_name || "—"}</div>
                  {h.formula_notes && (
                    <div className="mt-3 p-3 rounded-xl glass-subtle">
                      <div className="overline mb-1"><FlaskConical size={11} className="inline mr-1" /> Formula</div>
                      <div className="font-mono text-[12.5px] text-[var(--color-text-secondary)]">{h.formula_notes}</div>
                    </div>
                  )}
                  <div className="mt-3 flex items-center justify-between flex-wrap gap-2 font-inter text-[12.5px]">
                    <div>{(h.products_used || []).length} product{(h.products_used || []).length === 1 ? "" : "s"} used</div>
                    <div className="font-mono text-[var(--color-gold-deeper)]">₹{(h.amount_due || 0) - (h.discount_amount || 0)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "profile" && (
            <div className="mt-5 glass-primary rounded-[20px] p-6">
              {!client ? (
                <div className="font-inter text-[14px] text-[var(--color-text-muted)]">No profile linked to this appointment.</div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-display text-[18px] font-bold tracking-[-0.02em]">{client.name}</h3>
                    <button
                      type="button"
                      onClick={() => setEditClientOpen(true)}
                      data-testid="stylist-edit-client-btn"
                      className="btn-3d btn-glass-3d btn-sm"
                    >
                      <Pencil size={13} /> Edit profile
                    </button>
                  </div>
                  <BirthdaySection client={client} testid={`stylist-birthday-${client.id}`} />
                  {client.allergies && (
                    <div className="rounded-2xl p-4" style={{ background: "rgba(185,28,28,0.08)", border: "1px solid rgba(185,28,28,0.25)" }}>
                      <div className="flex items-center gap-2 font-sora font-semibold text-[var(--color-danger-deep)]">
                        <AlertTriangle size={16} /> Allergies
                      </div>
                      <div className="font-inter text-[14px] text-[var(--color-danger-deep)] mt-1">{client.allergies}</div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <Stat label="Skin type"          value={client.skin_type || "—"} />
                    <Stat label="Hair type"          value={client.hair_type || "—"} />
                    <Stat label="Scalp condition"    value={client.scalp_condition || "—"} />
                    <Stat label="Occupation"         value={client.occupation || "—"} />
                    <Stat label="Location"           value={client.location || "—"} />
                    <Stat label="Referred via"       value={client.referral_source || "—"} />
                    <Stat label="Loyalty points"     value={client.loyalty_points || 0} mono />
                    <Stat label="Total visits"       value={client.total_visits || 0} mono />
                    <Stat label="Lifetime spend"     value={`₹${(client.total_spend || 0).toLocaleString("en-IN")}`} mono />
                  </div>
                  {client.email && (
                    <div className="font-inter text-[12px] text-[var(--color-text-muted)]">
                      <span className="overline mr-2">Email</span>{client.email}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {(client.tags || []).map((t) => (
                      <span key={t} className="px-2.5 py-1 rounded-full bg-[var(--color-mauve)]/12 text-[var(--color-mauve-deep)] font-inter font-medium text-[11.5px] border border-[var(--color-mauve)]/30 uppercase tracking-wider">{t}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
      {editClientOpen && client && (
        <EditClientModal
          client={client}
          onClose={() => setEditClientOpen(false)}
          onSaved={(updated) => { setClient(updated); setEditClientOpen(false); }}
        />
      )}
    </RoleShell>
  );
}

function StylistPicker({ stylistList, activeStylistId, setActiveStylistId }) {
  const active = stylistList.find((s) => s.id === activeStylistId);
  return (
    <div className="glass-primary rounded-[20px] p-5">
      <div className="overline mb-2">Acting as</div>
      <select value={activeStylistId} onChange={(e) => setActiveStylistId(e.target.value)}
              className="w-full h-11 px-3 rounded-xl bg-white/70 border border-white/80 font-inter text-[14px] outline-none">
        {stylistList.map((s) => <option key={s.id} value={s.id}>{`${s.name} · ${s.role}`}</option>)}
      </select>
      {active && (
        <div className="mt-3 flex items-center gap-2 font-inter text-[12.5px] text-[var(--color-text-muted)]">
          <Award size={14} className="text-[var(--color-gold-dark)]" />
          {active.rating?.toFixed(1)} · {active.skills?.slice(0, 2).join(", ")}
        </div>
      )}
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 h-10 rounded-full font-sora font-semibold text-[13px] transition border ${active ? "bg-[var(--color-gold-mid)]/15 border-[var(--color-gold-mid)]/40 text-[var(--color-gold-deeper)]" : "glass-subtle border-white/60 text-[var(--color-text-muted)]"}`}
    >
      {children}
    </button>
  );
}

function ServiceChecklist({ services, chosen, setChosen, total }) {
  const grouped = useMemo(() => {
    const map = {};
    for (const s of services) (map[s.category] = map[s.category] || []).push(s);
    return map;
  }, [services]);

  const [custom, setCustom] = useState("");
  const [customPrice, setCustomPrice] = useState("");

  const toggle = (name) => setChosen((c) => c.includes(name) ? c.filter((x) => x !== name) : [...c, name]);

  return (
    <div className="glass-primary rounded-[20px] p-6">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div className="overline">Service checklist</div>
        <div className="font-mono text-[24px] text-[var(--color-gold-deeper)] leading-none">₹{total}</div>
      </div>
      <div className="space-y-4">
        {Object.entries(grouped).map(([cat, list]) => (
          <div key={cat}>
            <div className="font-sora font-semibold text-[11px] uppercase tracking-[0.22em] text-[var(--color-mauve)] mb-2">{cat}</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {list.map((s) => {
                const sel = chosen.includes(s.name);
                return (
                  <button key={s.id} onClick={() => toggle(s.name)}
                          className={`text-left px-3 py-2.5 rounded-xl border transition ${sel ? "bg-[var(--color-gold-mid)]/15 border-[var(--color-gold-mid)]/45" : "bg-white/55 border-white/80 hover:bg-white/80"}`}>
                    <div className="font-sora font-semibold text-[13px] flex items-center justify-between gap-2">
                      {s.name} {sel && <Check size={13} className="text-[var(--color-gold-deeper)]" />}
                    </div>
                    <div className="font-mono text-[12px] text-[var(--color-text-muted)] mt-0.5">₹{s.base_price} · {s.duration_minutes}m</div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex gap-2 flex-wrap">
        <input placeholder="Custom add-on" value={custom} onChange={(e) => setCustom(e.target.value)}
               className="h-10 px-3 rounded-xl bg-white/70 border border-white/80 font-inter text-[13px] outline-none flex-1 min-w-[180px]" />
        <input placeholder="₹" value={customPrice} onChange={(e) => setCustomPrice(e.target.value.replace(/\D/g, ""))}
               className="h-10 w-24 px-3 rounded-xl bg-white/70 border border-white/80 font-mono text-[13px] outline-none" />
        <button className="btn-3d btn-glass-3d btn-sm" onClick={() => {
          if (!custom.trim()) return;
          setChosen((c) => [...c, `${custom} (₹${customPrice || 0})`]);
          setCustom(""); setCustomPrice("");
        }}>Add</button>
      </div>
    </div>
  );
}

function ProductLogger({ inventory, products, setProducts, search, setSearch }) {
  const filtered = useMemo(() => {
    if (!search.trim()) return inventory.slice(0, 6);
    const q = search.toLowerCase();
    return inventory.filter((i) => i.name.toLowerCase().includes(q)).slice(0, 8);
  }, [inventory, search]);
  const addProduct = (item, qty) => {
    const q = parseFloat(qty);
    if (!q || q <= 0) return;
    setProducts((p) => [...p, { product_id: item.id, product_name: item.name, qty_used: q, unit: item.unit }]);
  };
  const remove = (idx) => setProducts((p) => p.filter((_, i) => i !== idx));
  return (
    <div className="glass-primary rounded-[20px] p-6">
      <div className="overline mb-2">Product usage</div>
      <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products to log usage…"
             className="w-full h-11 px-3 rounded-xl bg-white/70 border border-white/80 font-inter text-[13.5px] outline-none" />
      <div className="mt-3 space-y-2">
        {filtered.map((i) => (
          <ProductPickRow key={i.id} item={i} onAdd={(q) => addProduct(i, q)} />
        ))}
      </div>
      {products.length > 0 && (
        <div className="mt-4">
          <div className="overline mb-2">Logged</div>
          <div className="space-y-1.5">
            {products.map((p, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-2 rounded-xl bg-white/55 border border-white/80">
                <div className="font-inter text-[13px]">{p.product_name} · <span className="font-mono">{p.qty_used} {p.unit}</span></div>
                <button onClick={() => remove(i)} className="text-[var(--color-text-muted)] hover:text-[var(--color-danger)]">remove</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ProductPickRow({ item, onAdd }) {
  const [qty, setQty] = useState("");
  const low = item.stock_qty <= item.reorder_level;
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/55 border border-white/80">
      <div className="flex-1 min-w-0">
        <div className="font-sora font-semibold text-[13px] truncate">{item.name}</div>
        <div className="font-mono text-[11.5px] text-[var(--color-text-muted)]">{item.stock_qty} {item.unit} in stock {low && <span className="text-[var(--color-warning-deep)]">· low</span>}</div>
      </div>
      <input value={qty} onChange={(e) => setQty(e.target.value)} placeholder="qty"
             className="h-9 w-20 px-2 rounded-lg bg-white border border-white/80 font-mono text-[12.5px] outline-none" />
      <button onClick={() => { onAdd(qty); setQty(""); }} className="btn-3d btn-glass-3d btn-sm">Log</button>
    </div>
  );
}

function Stat({ label, value, mono }) {
  return (
    <div>
      <div className="overline mb-1">{label}</div>
      <div className={`text-[16px] ${mono ? "font-mono" : "font-sora font-semibold"}`}>{value}</div>
    </div>
  );
}


// =========================
// Edit Client Modal — full client profile update
// =========================
function EditClientModal({ client, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: client.name || "",
    phone: client.phone || "",
    gender: client.gender || "female",
    email: client.email || "",
    dob: client.dob || "",
    anniversary: client.anniversary || "",
    allergies: client.allergies || "",
    skin_type: client.skin_type || "",
    hair_type: client.hair_type || "",
    scalp_condition: client.scalp_condition || "",
    occupation: client.occupation || "",
    location: client.location || "",
    address: client.address || "",
    referral_source: client.referral_source || "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const submit = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone are required");
      return;
    }
    setBusy(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === "" ? null : v])
      );
      const r = await api.patch(`/clients/${client.id}`, payload);
      const updated = unwrap(r);
      toast.success(`Updated ${updated?.name || client.name}'s profile`);
      onSaved?.(updated);
    } catch (e) {
      const data = e?.response?.data?.error;
      toast.error(data?.message || "Could not update client", { description: data?.code || "" });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center anim-fade-in"
         style={{ background: "rgba(28,25,23,0.40)", backdropFilter: "blur(12px)" }}
         data-testid="stylist-edit-client-modal">
      <div className="glass-elevated rounded-[24px] p-6 w-[min(640px,calc(100vw-32px))] max-h-[88vh] overflow-auto anim-slide-up-lg space-y-3">
        <div className="flex items-start justify-between mb-1">
          <div>
            <div className="overline mb-1">Edit client</div>
            <h2 className="font-display text-[22px] font-bold tracking-[-0.02em]">{client.name}</h2>
            <p className="font-inter text-[12.5px] text-[var(--color-text-muted)] mt-0.5">{client.phone}</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full glass-subtle flex items-center justify-center" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <EditField label="Full name *">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   data-testid="edit-client-name-input" className={editInputCls} />
          </EditField>
          <EditField label="Phone *">
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                   data-testid="edit-client-phone-input" className={`${editInputCls} font-mono`} />
          </EditField>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <EditField label="Gender">
            <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}
                    data-testid="edit-client-gender-input" className={editInputCls}>
              <option value="female">Female</option>
              <option value="male">Male</option>
              <option value="other">Other</option>
            </select>
          </EditField>
          <EditField label="Email">
            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                   data-testid="edit-client-email-input" className={editInputCls} placeholder="name@example.com" />
          </EditField>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <EditField label="Date of birth">
            <input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })}
                   data-testid="edit-client-dob-input" className={`${editInputCls} font-mono`} />
          </EditField>
          <EditField label="Anniversary">
            <input type="date" value={form.anniversary} onChange={(e) => setForm({ ...form, anniversary: e.target.value })}
                   data-testid="edit-client-anniversary-input" className={`${editInputCls} font-mono`} />
          </EditField>
        </div>

        <div className="rounded-2xl p-3 space-y-3" style={{ background: "rgba(185,28,28,0.05)", border: "1px solid rgba(185,28,28,0.20)" }}>
          <EditField label="Allergies (visible to all staff)">
            <textarea value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })}
                      rows={2}
                      data-testid="edit-client-allergies-input"
                      placeholder="Nickel, PPD, fragrance…"
                      className={`${editInputCls} h-auto py-2`} />
          </EditField>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <EditField label="Skin type">
            <input value={form.skin_type} onChange={(e) => setForm({ ...form, skin_type: e.target.value })}
                   data-testid="edit-client-skin-input" className={editInputCls} placeholder="Combination, dry…" />
          </EditField>
          <EditField label="Hair type">
            <input value={form.hair_type} onChange={(e) => setForm({ ...form, hair_type: e.target.value })}
                   data-testid="edit-client-hair-input" className={editInputCls} placeholder="Wavy, fine…" />
          </EditField>
          <EditField label="Scalp condition">
            <input value={form.scalp_condition} onChange={(e) => setForm({ ...form, scalp_condition: e.target.value })}
                   data-testid="edit-client-scalp-input" className={editInputCls} placeholder="Sensitive, dandruff…" />
          </EditField>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <EditField label="Occupation">
            <input value={form.occupation} onChange={(e) => setForm({ ...form, occupation: e.target.value })}
                   data-testid="edit-client-occupation-input" className={editInputCls} />
          </EditField>
          <EditField label="Location / area">
            <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                   data-testid="edit-client-location-input" className={editInputCls} />
          </EditField>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <EditField label="Referral source">
            <select value={form.referral_source} onChange={(e) => setForm({ ...form, referral_source: e.target.value })}
                    data-testid="edit-client-referral-input" className={editInputCls}>
              <option value="">—</option>
              <option>Instagram</option>
              <option>Friend referral</option>
              <option>Walk-by</option>
              <option>Google</option>
              <option>Wedding planner</option>
              <option>WhatsApp</option>
              <option>Other</option>
            </select>
          </EditField>
          <EditField label="Address">
            <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                   data-testid="edit-client-address-input" className={editInputCls} />
          </EditField>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-3d btn-glass-3d btn-sm" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn-3d btn-gold-3d" onClick={submit} disabled={busy} data-testid="edit-client-save-btn">
            <Save size={14} /> {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

const editInputCls = "w-full h-11 px-3 rounded-xl bg-white/70 border border-white/80 font-inter text-[14px] text-[var(--color-text-primary)] outline-none focus:border-[var(--color-gold-mid)] focus:ring-2 focus:ring-[var(--color-gold-mid)]/20 transition";

function EditField({ label, children }) {
  return (
    <label className="block">
      <div className="font-inter text-[12.5px] font-medium text-[var(--color-text-secondary)] mb-1.5">{label}</div>
      {children}
    </label>
  );
}
